import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as EditEye } from '@assets/icons/eye-dark.svg';
import { ReactComponent as EditIcon } from '@assets/icons/edit.svg';

import CommonImage from '@components/CommonImage';
import DesignLink from '@components/DesignLink';
import OfferPriceListItem from '@components/offer-price/OfferPriceListItem';
import { toastSuccess } from '@components/Toast';
import ProductSlider from '@components/ProductSlider';
import ProductLock from '@components/ProductLock';

import ProductTags from '@features/collection/details/ProductTags';

import { storeProductData } from '@store/actions/DesignAction';

import { isShowDraftButton } from '@utils/helper';
import {
	checkAnyUserType,
	checkPrimaryUserType,
	convertDateToLocal,
	getDesignAvailabilityStatus,
	getProductStatusWiseColor,
	getShortName,
	getUserInfo,
	isRequestFromCosting,
	isRequestFromSample,
	replaceUnderLine,
	showDesignViewPage,
	TASK_PRIORITY_CLASS_NAME
} from '@utils/Util';

import { BASE_FRONT_END_URL, LIST_VIEW_TYPE } from '@/constants/constant';

import { ReactComponent as InfoIcon } from '@/assets/icons/exclamation-mark.svg';
import useWindowInnerWidth from '@/hooks/useWindowInnerWidth';
import CreateDesignOption from '@/components/CreateDesignOption';

const ALLOWED_ROLES = ['costing', 'photography'];

const DesignCard = ({
	productInfo,
	handleBeforeUnload,
	onSetAllSelectFalse,
	setDesignIdToView,
	onRemoveProductFromCollection,
	collectionId,
	onLikeProduct,
	requestFromParam,
	isShowPrice,
	openEditSetOrder,
	openDeleteSetOrder,
	requestFrom,
	draftProduct,
	defaultViewType,
	productWiseTags,
	fetchAllTags,
	setSelectedIds,
	updateTagList,
	showFabricStatus,
	onMarkProduct,
	setUnmarkProductId,
	setOpenConfirmation,
	offerPriceInfo,
	artworkSoldStatus,
	setProducts,
	getCollectionProducts,
	failedTaskUpdateIds = []
}) => {
	const selectedProductIds = useSelector((store) => store.product.selectedProductIds);
	const [isShowTags, setIsShowTags] = useState(false);
	const [product, setProduct] = useState({});
	const tagRef = useRef();
	const dispatch = useDispatch();
	const location = useLocation();
	const isPhotographyTeamMember = checkPrimaryUserType('PHOTOGRAPHY');
	const isCollectionViewPage = location.pathname.includes('/collections/view/');
	const navigate = useNavigate();
	const [selectedProductToCreateOptn, setSelectedProductToCreateOptn] = useState(null);

	useEffect(() => {
		document.addEventListener('click', onOutSideClick);
		return () => {
			document.removeEventListener('click', onOutSideClick);
		};
	}, [isShowTags]);

	const onOutSideClick = (event) => {
		if (isShowTags && tagRef && !tagRef.current?.contains(event.target)) {
			setIsShowTags(false);
		}
	};

	useEffect(() => {
		setIsShowTags(false);
	}, [defaultViewType]);

	useEffect(() => {
		setProduct(productInfo);
	}, [productInfo]);

	const onDesignEditClick = (productId, param = '') => {
		let url = `/designs/edit/${productId}`;
		if (param && !isPhotographyTeamMember) {
			url += `?activeTab=${param}`;
		}
		if (isPhotographyTeamMember) {
			url += `?activeTab=presentation`;
		}

		let currentPage = location.pathname + location.search;
		localStorage.setItem('fromRoute', currentPage);
		handleBeforeUnload();
		window.open(url, '_blank');
	};

	const toggleSelect = async (productId) => {
		let tmpSelectedProductIds = [...selectedProductIds];
		if (selectedProductIds.includes(productId)) {
			tmpSelectedProductIds = selectedProductIds.filter((id) => id !== productId);
			onSetAllSelectFalse();
		} else {
			tmpSelectedProductIds.push(productId);
		}
		if (requestFrom === 'SAMPLE_DEVELOPMENT') {
			setSelectedIds(tmpSelectedProductIds);
		}
		await dispatch(storeProductData('selectedProductIds', tmpSelectedProductIds));
	};

	const onClickCollection = (e, item) => {
		e.stopPropagation();
		window.open(`/collections/view/${item.collectionId}`);
	};

	const handleSoldArtworkClick = () => {
		const getProductId = artworkSoldStatus?.artworkProductResponseList?.find(
			(item) => item?.productId === product?.id
		)?.productId;
		if (getProductId)
			window.open(
				`/designs/edit/${artworkSoldStatus?.artworkProductResponseList?.find((item) => item?.productId === product?.id)?.productId}?designToolsActiveTab=files`,
				'_blank'
			);
	};

	const handleProductUpdate = (updatedProducts) => {
		let selectedProduct = updatedProducts[0];
		if (selectedProduct && setProducts) {
			setProducts((prevState) => {
				return prevState.map((prod) =>
					prod.id === selectedProduct.productId
						? { ...prod, isLocked: selectedProduct.isLocked }
						: prod
				);
			});
		}
	};

	const { width } = useWindowInnerWidth();

	const getCharacterLimit = (windowWidth, isNarrowView = false) => {
		if (isNarrowView) {
			if (windowWidth >= 1440) return 30;
			if (windowWidth >= 1360) return 25;
			if (windowWidth >= 1280) return 25;
			if (windowWidth >= 991) return 22;
			if (windowWidth >= 660) return 25;
			if (windowWidth >= 570) return 30;

			return 35;
		}

		if (windowWidth >= 1441) return 35;
		if (windowWidth >= 1366) return 35;
		if (windowWidth >= 1280) return 40;
		if (windowWidth >= 1200) return 35;
		if (windowWidth >= 991) return 45;
		if (windowWidth >= 660) return 25;
		if (windowWidth >= 570) return 70;
		if (windowWidth >= 365) return 40;
		return 30;
	};

	const renderProductImageSlider = (product) => {
		const renderActionClick = () => {
			if (!product.isBasicInfoUpdated && !isRequestFromSample() && !isRequestFromCosting()) {
				/* Empty Block */
			} else if (isRequestFromCosting() || (showDesignViewPage(product) && !isRequestFromSample())) {
				setDesignIdToView(product, false);
			} else {
				let param = isRequestFromSample() ? 'presentation' : 'design';
				onDesignEditClick(product.id, param);
			}
		};

		const onSetDetailsClick = (productId) => {
			let url = `/set/view/${productId}`;
			let currentPage = location.pathname + location.search;
			localStorage.setItem('fromRoute', currentPage);
			handleBeforeUnload();
			navigate(url);
		};

		if (product?.setPackStyleFlag === 'SET_STYLE') {
			return (
				<>
					{(product?.designDocuments || []).slice(0, 2).map((item) => (
						<CommonImage
							onClick={() => onSetDetailsClick(product.id)}
							key={item?.id}
							src={item?.docUrl}
							className="card-image"
							style={{ height: '50%', width: '100%' }}
							alt="product-image"
							isStatic={false}
						/>
					))}
				</>
			);
		} else {
			return (
				<ProductSlider
					presentationWiseImages={product?.productPresentationResponseList || []}
					onClick={renderActionClick}
					designDocuments={product?.designDocuments || []}
				/>
			);
		}
	};

	const renderBasicInfo = (product, flag) => (
		<>
			<div className="col-md-3 px-2 single-item">
				<div className="single-design-card with-editable shadow-1dp ">
					<div
						className={`card-top ${failedTaskUpdateIds.includes(product.id) ? 'task-incomplete' : ''}`}
					>
						<div className="edit-items">
							<div className="select-and-edit d-flex justify-content-between">
								<div className="custom-chekbox">
									<div
										className="form-group"
										onClick={() => {
											onRemoveProductFromCollection(
												collectionId,
												product.id,
												product?.isBlock
											);
										}}
									>
										<CommonImage src="/icons/delete24.svg" alt="delete" />
									</div>
								</div>
							</div>
						</div>
						{renderProductImageSlider(product, flag)}
						<span
							className="update-basic-info-btn single-style-btn"
							onClick={(event) => {
								event.stopPropagation();
								setDesignIdToView(product, true, true, true);
							}}
						>
							Update
						</span>
						{product?.isLocked && (
							<ProductLock
								lockedBy={product.lockedBy}
								lockedByName={product.lockedByName}
								lockedDate={product.lockedDate}
								callBack={handleProductUpdate}
								productId={product.id}
							/>
						)}
					</div>
					<div className="card-content">
						<div className="card-info">
							<p className="status">
								<span
									className={`${getDesignAvailabilityStatus(product?.availabilityStatus)}`}
								>
									{replaceUnderLine(product?.availabilityStatus)}
								</span>
							</p>
							<Tooltip
								title={<span className="regular-14">{product.name}</span>}
								placement="top"
								arrow
							>
								<span className="regular-14 gray_dark_03">
									{getShortName(product?.name, 20)}
								</span>
							</Tooltip>
						</div>
					</div>
				</div>
			</div>
		</>
	);

	const getBrandLocationList = (locations = []) => {
		const locationInWord = locations.join(', ');
		const shortName = getShortName(locationInWord, 15);
		return (
			<Tooltip title={locationInWord} placement="top" arrow>
				<li>{shortName}</li>
			</Tooltip>
		);
	};

	const isRequestFromSuggestedBuyer = () => {
		let paramList = new URLSearchParams(location.search);
		const requestFrom = paramList.get('status');
		return requestFrom === 'SUGGESTED_FOR_BUYER';
	};

	const getSetmembersStatus = (members = []) => {
		const statusList = [];
		members?.forEach((member) => {
			statusList.push(...(Array.isArray(member?.productStatusList) ? member.productStatusList : []));
		});
		return statusList;
	};

	const renderCardInfoWithoutNarrowView = () => {
		if (defaultViewType !== LIST_VIEW_TYPE.NARROW_VIEW) {
			return (
				<div className="card-content">
					<div className="card-info mb-3">
						<div className="d-flex justify-content-between">
							<DesignLink
								id={product?.id}
								onClickDesignView={() => setDesignIdToView(product, false)}
							>
								<h4 className="regular-12 weight-500 gray_dark_01 pt-2 mb-0">
									{product?.referenceNumber}
								</h4>
							</DesignLink>
						</div>
						<div className="status d-flex justify-content-between pl-0 mt-2">
							{!isRequestFromSuggestedBuyer() && (
								<div className="d-flex flex-wrap gap-3">
									{product.availabilityStatus === 'SOLD' &&
										requestFrom !== 'SET_ORDER_DETAILS' && (
											<>
												<span className="sold d-flex align-items-center justify-content-center gap-2">
													SOLD{' '}
													<span className="count">
														{product?.numberOfResold}
													</span>{' '}
												</span>
												<ul className="solded-items shadow-4dp">
													{product?.orderRefNumbers?.map((item) => (
														<li key={item.orderId}>
															<Link
																to={`/orders/view/${item.orderId}`}
																key={item.orderId}
															>
																{item?.orderRefNumber}{' '}
																{item?.poItemIdentifierCodeList?.length > 0
																	? `(${item?.poItemIdentifierCodeList.join(', ')})`
																	: ''}
															</Link>
														</li>
													))}
												</ul>
											</>
										)}
									<div className="status-content d-flex flex-column gap-1">
										{renderProductStatus(
											product?.isSet
												? getSetmembersStatus(product?.setMemberProducts)
												: product?.productStatusList
										)}{' '}
									</div>
								</div>
							)}
							{/* <div className="add-tag ml-1">
								<button
									className="button text-only d-flex"
									onClick={(e) => {
										e.stopPropagation();
										setIsShowTags(true);
									}}
								>
									<CommonImage src="/icons/plus.svg" alt="plus" />{' '}
									<span className="gray_dark_03 regular-12">Add Tag </span>
								</button>
							</div> */}
						</div>
						{product?.tagResponseList?.length > 0 && (
							<div className="d-flex justify-content-between my-2">
								<div>
									<div
										className="tags design-tags"
										onClick={(e) => {
											e.stopPropagation();
											setIsShowTags(true);
										}}
									>
										{renderProductTags()}
									</div>
								</div>
							</div>
						)}
						{isShowTags && (
							<div ref={tagRef}>
								<ProductTags
									currentProductTagList={product.tagResponseList}
									productId={product.id}
									allTagList={productWiseTags}
									fetchAllTags={fetchAllTags}
									updateTagList={updateTagList}
								/>
							</div>
						)}

						<ul className="common-list-component mt-1">
							{product?.subCategory && (
								<li>
									<Tooltip title={product?.subCategory} placement="top" arrow>
										<span className="regular-12 gray_dark_02">
											{getShortName(product?.subCategory, 25)}
										</span>
									</Tooltip>
								</li>
							)}
							{product?.addedByName && (
								<li className="text-uppercase">
									<Tooltip
										title={<span className="regular-14">{product.addedByName}</span>}
										placement="top"
										arrow
									>
										<span className="regular-12 gray_dark_02">
											{getShortName(product.addedByName, 10)}
										</span>
									</Tooltip>
								</li>
							)}
							{product?.addedAt && (
								<li>
									<span>
										{convertDateToLocal(product.addedAt, 'DD MMM')?.toUpperCase()}
									</span>
								</li>
							)}
						</ul>
						{((product?.developmentLocations && requestFrom !== 'SAMPLE_DEVELOPMENT') ||
							product?.basePrice ||
							offerPriceInfo?.length > 0) && (
							<ul className="common-list-component mt-1">
								{product?.developmentLocations &&
									getBrandLocationList(product?.developmentLocations)}
								{product?.basePrice && isShowPrice && (
									<li>
										<span className="mb-0 semibold-12">
											{'P:$' + parseFloat(product.basePrice).toFixed(2)}
										</span>
									</li>
								)}
								{offerPriceInfo?.length > 0 && isShowPrice && (
									<OfferPriceListItem offerPriceInfo={offerPriceInfo} />
								)}
							</ul>
						)}
						{!isCollectionViewPage && (
							<p
								className="collection-name regular-12 gray_dark_02 text-uppercase pt-1"
								onClick={(e) => onClickCollection(e, product)}
							>
								{product?.collectionName}
							</p>
						)}
					</div>
				</div>
			);
		}
	};

	const renderProductTags = () => {
		return product?.tagResponseList?.map((item, index) => {
			return (
				<span
					key={`all_tags_${index}`}
					style={{ background: item.colorCode }}
					className="status radius without-nowrap"
				>
					{item.text}
				</span>
			);
		});
	};

	const renderProductStatus = (statusList = [], isNarrowView = false) => {
		const charLimit = getCharacterLimit(width, isNarrowView);
		return statusList?.map((item) => {
			return (
				<>
					<span
						className={`status custom-tooltip status-with-border ${getProductStatusWiseColor[item?.status] || 'yellow-1'} mr-1`}
						key={`status_${item?.status}`}
					>
						{item?.status?.length > charLimit
							? `${replaceUnderLine(item?.status?.slice(0, charLimit))}...`
							: replaceUnderLine(item?.status)}{' '}
						<span className="tooltiptext">{replaceUnderLine(item?.status)}</span>
					</span>
				</>
			);
		});
	};

	const renderCardInfoForNarrowView = () => {
		if (defaultViewType === LIST_VIEW_TYPE.NARROW_VIEW) {
			return (
				<div className="card-content">
					<div className="card-info">
						<h4 className="regular-12 weight-500 gray_dark_01 pt-2">
							{product?.referenceNumber}
						</h4>
						<div className="d-flex flex-wrap gap-1 status-content">
							{renderProductStatus(product?.productStatusList, true)}
						</div>
						<div className="d-flex justify-content-between my-2">
							<div>
								<div className="tags design-tags" onClick={() => setIsShowTags(true)}>
									{renderProductTags()}
								</div>
							</div>
							<div className="add-tag ml-1">
								<button className="button text-only" onClick={() => setIsShowTags(true)}>
									<CommonImage src="/icons/plus.svg" alt="plus" />{' '}
									<span className="gray_dark_03 regular-12">Add Tag</span>
								</button>
							</div>
						</div>
					</div>
					{isShowTags && (
						<div ref={tagRef}>
							<ProductTags
								currentProductTagList={product.tagResponseList}
								productId={product.id}
								allTagList={productWiseTags}
								fetchAllTags={fetchAllTags}
								updateTagList={updateTagList}
							/>
						</div>
					)}
				</div>
			);
		}
	};

	// const isShowDraftButton = (product, requestFrom) => {
	// 	if (product?.productStatusList && !product?.isSet && requestFrom !== 'SET_ORDER_DETAILS') {
	// 		for (let i = 0; i < product?.productStatusList.length; i++) {
	// 			if (draftButtonHiddenStatuses.includes(product?.productStatusList[i].status)) {
	// 				return false;
	// 			}
	// 		}
	// 	}
	// 	return true;
	// };

	const isAllowedMarkAsBlock = () => {
		return checkAnyUserType(['PATTERN_MASTER', 'ADMIN', 'GARMENTS_TECH', 'FASHION_DESIGNER']);
	};

	let flag = 1;
	return product?.isBasicInfoUpdated === false ? (
		renderBasicInfo(product, flag)
	) : (
		<div
			key={`Id-${product.id}`}
			className={
				product.isAddedToList
					? `single-design-card with-editable hovered shadow-1dp ${product?.isSet ? 'set' : ''}`
					: `single-design-card with-editable shadow-1dp ${product?.liked ? 'favorited' : ''} ${
							product?.isSet ? 'set' : ''
						}`
			}
		>
			<div className={`card-top ${failedTaskUpdateIds.includes(product.id) ? 'task-incomplete' : ''}`}>
				<div className="tp-hold-and-status d-flex align-items-center gap-2">
					{product?.isHoldForIssue && (
						<div className="update-basic-info-btn tp-hold">
							<p className="regular-12 gray_dark_01 white-space-nowrap">
								<InfoIcon /> <span>TP hold</span>
							</p>
						</div>
					)}
					{product?.taskPriority && (
						<div className="status-tag">
							<span className={TASK_PRIORITY_CLASS_NAME[product?.taskPriority]}>
								{product?.taskPriority}
							</span>
						</div>
					)}
				</div>

				<div className="edit-items">
					<div className="select-and-edit d-flex justify-content-between">
						<div className={`custom-chekbox`} onClick={(e) => e.stopPropagation()}>
							<div className="form-group d-flex">
								{!isRequestFromCosting() &&
									!isRequestFromSample() &&
									requestFrom !== 'SET_ORDER_DETAILS' && (
										<>
											<input
												type="checkbox"
												id={product.id}
												name="productId"
												value={product.id}
												onChange={() => toggleSelect(product.id)}
												checked={!!product.isSelected}
											/>
											<label htmlFor={product.id} />
										</>
									)}
								{requestFrom !== 'SAMPLE_DEVELOPMENT' && (
									<>
										{/* {product?.liked === true ? (
											<img
												src={`${process.env.PUBLIC_URL}/icons/favorite.svg`}
												alt="favourite"
												className="add-to-favorite"
												onClick={() => {
													onUnlikeProduct && onUnlikeProduct(product.id);
												}}
											/>
										) : (
											<img
												src={`${process.env.PUBLIC_URL}/icons/not-favorite.svg`}
												alt="not-favourite"
												className="add-to-favorite"
												onClick={() => {
													onLikeProduct(product.id);
												}}
											/>
										)} */}
										<CommonImage
											src="/icons/not-favorite.svg"
											alt="not-favourite"
											className="add-to-favorite"
											onClick={() => {
												onLikeProduct(product.id);
											}}
										/>
									</>
								)}

								{showFabricStatus && (
									<Tooltip
										title="Please add body fabric for place order."
										placement="top"
										arrow
									>
										<CommonImage
											className="add-to-favorite ml-1"
											src={`/images/icons/delivery-status-icon.svg`}
											alt=""
										/>
									</Tooltip>
								)}
							</div>
						</div>

						<div className={`d-flex justify-content-between`}>
							{!product?.isSet && requestFrom !== 'SAMPLE_DEVELOPMENT' && (
								<div>
									<Tooltip title="Edit" placement="top" arrow>
										<span
											data-dismiss="modal"
											onClick={(e) => {
												e.stopPropagation();
												onDesignEditClick(product.id);
											}}
											className={`edit-design-icon ${product?.isLocked ? 'cursor-none' : ''}`}
										>
											<EditIcon />
										</span>
									</Tooltip>
								</div>
							)}
							{product?.isSet && requestFrom !== 'SAMPLE_DEVELOPMENT' && (
								<div
									data-toggle="modal"
									data-target={'#designViewModal'}
									onClick={() => {
										setDesignIdToView(product, false);
									}}
								>
									<span className="view-icon">
										<EditEye />
									</span>
								</div>
							)}

							{requestFrom !== 'SAMPLE_DEVELOPMENT' && (
								<div className="option create-option-dropdow">
									<div className="dropdown">
										<button
											className="btn btn-default dropdown-toggle"
											type="button"
											id={`more_menu_${product.id}`}
											data-toggle="dropdown"
											aria-expanded="false"
											// onClick={(e) => e.stopPropagation()}
										>
											<CommonImage src="/icons/more_icon.svg" alt="more" />
										</button>
										<ul
											className="dropdown-menu dropdown-menu-right scroll-y-label shadow-lg"
											role="menu"
											aria-labelledby={`more_menu_${product.id}`}
										>
											{product.isSet && (
												<>
													<li
														role="presentation"
														className="border-top-solid px-4 pb-3"
													>
														<span
															role="menuitem"
															tabIndex="-1"
															className="font-weight-normal"
															onClick={(e) => {
																e.stopPropagation();
																openEditSetOrder('SET', product);
															}}
														>
															Edit Set
														</span>
													</li>
													<li
														role="presentation"
														className="border-top-solid px-4 pb-3"
													>
														<span
															style={{ color: 'red' }}
															role="menuitem"
															tabIndex="-1"
															className="font-weight-normal red"
															onClick={(e) => {
																e.stopPropagation();
																openDeleteSetOrder('SET', product);
															}}
														>
															Delete Set
														</span>
													</li>
												</>
											)}
											{!product?.isSet && (
												<>
													<li role="presentation" className="px-4 pb-3 pt-3">
														<span
															role="menuitem"
															tabIndex="-1"
															className="font-weight-normal  text-black"
															onClick={() => {
																navigator.clipboard.writeText(
																	`${BASE_FRONT_END_URL}/collections/list?openDesign=true&productId=${product.id}`
																);
																toastSuccess('Copied');
															}}
														>
															Copy design link
														</span>
													</li>
													<li role="presentation" className="px-4 pb-3 pt-3">
														<span
															role="menuitem"
															tabIndex="-1"
															className="font-weight-normal  text-black"
															onClick={() => {
																navigator.clipboard.writeText(
																	product?.referenceNumber
																);
																toastSuccess('Copied');
															}}
														>
															Copy design number
														</span>
													</li>
													<li role="presentation" className="px-4 pb-3 pt-3">
														<span
															role="menuitem"
															tabIndex="-1"
															className="font-weight-normal  text-black"
															onClick={() =>
																setDesignIdToView(product, true, true)
															}
														>
															Edit basic info
														</span>
													</li>
													{requestFrom !== 'SET_ORDER_DETAILS' &&
														isAllowedMarkAsBlock() && (
															<li
																role="presentation"
																className="px-4 pb-3 pt-3"
															>
																<span
																	role="menuitem"
																	tabIndex="-1"
																	className="font-weight-normal text-black"
																	onClick={() => {
																		onMarkProduct(
																			product.id,
																			product?.isBlock ? 'edit' : 'add',
																			product?.parentCatergory
																		);
																	}}
																>
																	{product?.isBlock
																		? 'Edit block info'
																		: 'Mark as a block'}
																</span>
															</li>
														)}

													{(getUserInfo()?.id === product?.blockCreatedBy?.id ||
														checkAnyUserType(['ADMIN', 'GARMENTS_TECH'])) &&
														product?.isBlock && (
															<li
																role="presentation"
																className="px-4 pb-3 pt-3"
															>
																<span
																	role="menuitem"
																	tabIndex="-1"
																	className="font-weight-normal  text-black"
																	onClick={() => {
																		setUnmarkProductId(product?.id);
																		setOpenConfirmation(true);
																	}}
																>
																	Unmark as block
																</span>
															</li>
														)}

													{!ALLOWED_ROLES.includes(requestFromParam) &&
														requestFrom !== 'SET_ORDER_DETAILS' && (
															<>
																<li role="presentation" className="px-4 pb-3">
																	<span
																		role="menuitem"
																		tabIndex="-1"
																		className="font-weight-normal  text-black"
																		onClick={(e) => {
																			e.stopPropagation();
																			setSelectedProductToCreateOptn(
																				product
																			);
																		}}
																	>
																		Creat design option
																	</span>
																</li>

																{/* <li role="presentation" className="px-4 pb-3">
																	<span
																		style={{ color: 'red' }}
																		role="menuitem"
																		tabIndex="-1"
																		className="font-weight-normal red"
																		onClick={(e) => {
																			e.stopPropagation();
																			onRemoveProductFromCollection(
																				collectionId,
																				product.id,
																				product?.isBlock
																			);
																		}}
																	>
																		Delete
																	</span>
																</li> */}
															</>
														)}
													{product?.isPack && (
														<>
															<li
																role="presentation"
																className="border-top-solid px-4 pb-3"
															>
																<span
																	style={{ color: 'red' }}
																	role="menuitem"
																	tabIndex="-1"
																	className="font-weight-normal red"
																	onClick={(e) => {
																		e.stopPropagation();
																		openDeleteSetOrder('PACK', product);
																	}}
																>
																	Delete Pack
																</span>
															</li>
														</>
													)}
													<span className="gray_dark_02 d-block px-3 pt-2 border-top-solid">
														COPIED FROM
													</span>
													<li role="presentation" className="px-4 pb-3">
														<div className="d-flex justify-content-between">
															<p
																role="menuitem"
																tabIndex="-1"
																className="copy-span"
																data-toggle="modal"
																data-target={'#designViewModal'}
																onClick={(event) => {
																	event.stopPropagation();
																	setDesignIdToView(product, true);
																}}
															>
																<span className="text-black">
																	{product?.referenceNumber}
																</span>
															</p>
															<span className="copy-style">
																<Tooltip title="Copy" placement="top" arrow>
																	<CommonImage
																		onClick={(e) => {
																			e.stopPropagation();
																			navigator.clipboard.writeText(
																				`${product?.referenceNumber}`
																			);
																			toastSuccess('Copied');
																		}}
																		src={`/icons/copy-gray.svg`}
																		alt="copy"
																		className="copy-icon cursor-pointer"
																	/>
																</Tooltip>
															</span>
														</div>
													</li>

													{product?.parentProduct && (
														<li role="presentation" className="px-4 pb-3">
															<div className="d-flex justify-content-between">
																<p
																	role="menuitem"
																	tabIndex="-1"
																	className="copy-span"
																	data-toggle="modal"
																	data-target={'#designViewModal'}
																	onClick={(event) => {
																		event.stopPropagation();
																		setDesignIdToView(
																			product?.parentProduct,
																			true
																		);
																	}}
																>
																	<span className="text-black">
																		{
																			product?.parentProduct
																				?.referenceNumber
																		}
																	</span>
																</p>
																<span className="copy-style">
																	<Tooltip
																		title="Copy"
																		placement="top"
																		arrow
																	>
																		<CommonImage
																			onClick={(e) => {
																				e.stopPropagation();
																				navigator.clipboard.writeText(
																					`${product?.parentProduct?.referenceNumber}`
																				);
																				toastSuccess('Copied');
																			}}
																			src={`/icons/copy-gray.svg`}
																			alt="copy"
																			className="copy-icon cursor-pointer"
																		/>
																	</Tooltip>
																</span>
															</div>
														</li>
													)}
													{product?.options && (
														<>
															<span className="gray_dark_02 d-block px-3 py-2">
																OPTIONS
															</span>
															{product?.options?.map((option) => (
																<li
																	role="presentation"
																	className="px-4 pb-3"
																	key={option.id}
																>
																	<div className="d-flex justify-content-between">
																		<p
																			role="menuitem"
																			tabIndex="-1"
																			className="copy-span"
																			data-toggle="modal"
																			data-target={'#designViewModal'}
																			onClick={(event) => {
																				event.stopPropagation();
																				setDesignIdToView(
																					option,
																					true
																				);
																			}}
																		>
																			<span className="text-black">
																				{option?.referenceNumber}
																			</span>
																		</p>
																		<span className="copy-style">
																			<Tooltip
																				title="Copy"
																				placement="top"
																				arrow
																			>
																				<CommonImage
																					onClick={(e) => {
																						e.stopPropagation();
																						navigator.clipboard.writeText(
																							`${product?.parentProduct?.referenceNumber}`
																						);
																						toastSuccess(
																							'Copied'
																						);
																					}}
																					src={`/icons/copy-gray.svg`}
																					alt="copy"
																					className="copy-icon cursor-pointer"
																				/>
																			</Tooltip>
																		</span>
																	</div>
																</li>
															))}
														</>
													)}
												</>
											)}
										</ul>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
				<div className="product-status d-flex align-items-end gap-1">
					{artworkSoldStatus?.status === 'SOLD' ? (
						<div className="sold-status" onClick={handleSoldArtworkClick}>
							<span className="status red-3 d-flex align-items-center">
								Artwork Sold
								<CommonImage className="ml-1" src="/icons/link-white.svg" />
							</span>
						</div>
					) : null}

					{product?.removedFromFavourite && (
						<div>
							<span className="status red-3">Not Selected</span>
						</div>
					)}
					{product?.isPack && (
						<div>
							<span className="status pack">Pack</span>
						</div>
					)}
					{product?.isSet && (
						<div>
							<span className="status set">Set</span>
						</div>
					)}

					{product?.photographyUpdated === true && isRequestFromSample() && (
						<CommonImage
							src="/icons/PhotographySuccess.svg"
							alt="photography"
							className="photography-done"
						/>
					)}
					{product?.costingCompleted === true && isRequestFromCosting() && (
						<CommonImage
							src="/icons/PhotographySuccess.svg"
							alt="photography"
							className="photography-done"
						/>
					)}
					{product?.isBlock && (
						<div>
							<span className="status yellow-1 rounded">BLOCK</span>
						</div>
					)}
				</div>
				{requestFrom !== 'SAMPLE_DEVELOPMENT' && renderProductImageSlider(product, flag)}
				{requestFrom === 'SAMPLE_DEVELOPMENT' && (
					<CommonImage
						key={product?.id}
						src={product?.featureImageDocUrl}
						className="card-image"
						alt="product-image_isBasicInfoUpdated_miss"
						isStatic={false}
					/>
				)}
				{requestFrom !== 'SET_ORDER_DETAILS' && isShowDraftButton(product, requestFrom) && (
					<button className="action-btn" onClick={() => draftProduct(product)}>
						<CommonImage src="/icons/minus.svg" alt="plus" />
					</button>
				)}

				{product?.isChangeRequired && (
					<span className="sold-design-tag regular-14">
						<CommonImage src="/icons/alert-octagon-gray.svg" alt="alert" />
						Please make changes to avail this design
					</span>
				)}

				{product?.hasUnseenMention && (
					<div className="comment-unread">
						<CommonImage src="/icons/comment-not-read.svg" alt="comment" />
					</div>
				)}
				{product?.isLocked && (
					<ProductLock
						lockedBy={product.lockedBy}
						lockedByName={product.lockedByName}
						lockedDate={product.lockedDate}
						callBack={handleProductUpdate}
						productId={product.id}
					/>
				)}
			</div>
			{renderCardInfoWithoutNarrowView()}
			{renderCardInfoForNarrowView()}
			{selectedProductToCreateOptn && (
				<CreateDesignOption
					onClose={() => setSelectedProductToCreateOptn(null)}
					onSuccess={() => getCollectionProducts(0)}
					designDetails={selectedProductToCreateOptn}
				/>
			)}
		</div>
	);
};

export default DesignCard;
